import { CircleNotch, MagnifyingGlass } from "@phosphor-icons/react"
import { type DialogProps } from "@radix-ui/react-dialog"
import { Command as CommandPrimitive } from "cmdk"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { inputVariant } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type CommandDialogProps = DialogProps

export const Command = ({ className, ...props }: React.ComponentPropsWithRef<typeof CommandPrimitive>) => (
  <CommandPrimitive
    className={cn(
      "bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md",
      className,
    )}
    {...props}
  />
)

export const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <Command className="**:[[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group]]:px-2 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  )
}

interface CommandInputProps extends React.ComponentPropsWithRef<typeof CommandPrimitive.Input> {
  isLoading?: boolean
}

export const CommandInput = ({ className, isLoading, ...props }: CommandInputProps) => (
  // oxlint-disable-next-line react/no-unknown-property
  <div className="relative" cmdk-input-wrapper="">
    <CommandPrimitive.Input
      className={cn(
        inputVariant(),
        "text-on-surface placeholder:text-on-surface-subdued pt-0 text-sm placeholder:text-sm",
        className,
      )}
      {...props}
    />
    <div className="absolute top-1/2 right-1 -translate-y-1/2 p-2">
      {isLoading ? (
        <CircleNotch className="text-on-surface-subdued animate-spin" />
      ) : (
        <MagnifyingGlass className="text-on-surface-subdued" />
      )}
    </div>
  </div>
)

export const CommandList = ({ className, ...props }: React.ComponentPropsWithRef<typeof CommandPrimitive.List>) => (
  <CommandPrimitive.List className={cn("max-h-75 overflow-x-hidden overflow-y-auto px-2", className)} {...props} />
)

export const CommandEmpty = ({ className, ...props }: React.ComponentPropsWithRef<typeof CommandPrimitive.Empty>) => (
  <CommandPrimitive.Empty className={cn("py-6 text-center text-sm", className)} {...props} />
)

export const CommandGroup = ({ className, ...props }: React.ComponentPropsWithRef<typeof CommandPrimitive.Group>) => (
  <CommandPrimitive.Group
    className={cn(
      "text-foreground **:[[cmdk-group-heading]]:text-on-surface-subdued overflow-hidden **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-2.5 **:[[cmdk-group-heading]]:text-sm **:[[cmdk-group-heading]]:font-medium",
      className,
    )}
    {...props}
  />
)

export const CommandSeparator = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof CommandPrimitive.Separator>) => (
  <CommandPrimitive.Separator className={cn("bg-border -mx-2 my-2.5 h-px", className)} {...props} />
)

export const CommandItem = ({ className, ...props }: React.ComponentPropsWithRef<typeof CommandPrimitive.Item>) => (
  <CommandPrimitive.Item
    className={cn(
      "aria-selected:bg-surface-hovered active:bg-surface-subdued-pressed relative flex cursor-default items-center rounded-lg px-2 py-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50",
      className,
    )}
    {...props}
  />
)

export const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn("text-muted-foreground ml-auto text-xs tracking-widest", className)} {...props} />
}
